/**
 * @fileoverview Task dependencies section (MOR-696 Stage 3)
 *
 * Renders the "Blocked by" (incoming) and "Blocks" (outgoing) dependency edges
 * for a persisted task inside the key-date side panel, with add/remove controls.
 * A task is blocked while any of its "blocked by" tasks are not yet terminal.
 *
 * Dependencies require persisted task ids on both ends, so derived statutory
 * rows that haven't been materialised yet show a hint instead of the editor.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { Ban, CheckCircle2, ChevronDown, Link2, Plus, X } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { extractErrorMessage } from "@/utils/errors";

import type { CalendarEventRow } from "@/calendar/eventAdapter";
import { useTaskPanelNav } from "@/calendar/task-panel-nav";

export function TaskDependenciesSection({
  event,
}: {
  event: CalendarEventRow;
}) {
  const taskId = event.persistedId ?? null;

  const deps = useQuery(
    api.tasks.dependencies.listForTask,
    taskId ? { taskId } : "skip",
  );
  const jobEvents = useQuery(api.keyDates.queries.listForJob, {
    jobId: event.jobId,
  });

  const createDep = useMutation(api.tasks.dependencies.create);
  const removeDep = useMutation(api.tasks.dependencies.remove);
  const materialise = useMutation(api.keyDates.mutations.materialise);

  const nav = useTaskPanelNav();

  // Resolve a linked task's full calendar row so clicking it can open a
  // side-by-side column. Statutory rows that haven't materialised yet won't
  // have a `persistedId` and are simply not clickable.
  const rowByPersistedId = useMemo(() => {
    const map = new Map<string, CalendarEventRow>();
    for (const e of jobEvents ?? []) {
      if (e.persistedId) map.set(e.persistedId.toString(), e);
    }
    return map;
  }, [jobEvents]);

  const openRelated = (linkedTaskId: string) => {
    const row = rowByPersistedId.get(linkedTaskId);
    if (row && nav) nav.openRelated(row);
  };

  const [addingKind, setAddingKind] = useState<"blockedBy" | "blocks" | null>(
    null,
  );
  // Dependencies are rarely used, so the editor stays collapsed to a single
  // header line until there's something to show or the user opts in.
  const [expanded, setExpanded] = useState(false);

  // Ids already linked in either direction (plus self) are excluded from the
  // picker so the user can't create duplicate or self edges.
  const linkedIds = useMemo(() => {
    const set = new Set<string>();
    if (taskId) set.add(taskId.toString());
    for (const e of deps?.blockedBy ?? []) set.add(e.task.id.toString());
    for (const e of deps?.blocks ?? []) set.add(e.task.id.toString());
    return set;
  }, [deps, taskId]);

  // Every other task on the job is a candidate — derived statutory rows
  // (no persisted id yet) carry a `derived::<code>` value and are
  // materialised when picked (MOR-707).
  const candidateOptions = useMemo(
    () =>
      (jobEvents ?? [])
        .filter(
          (e) =>
            e.id !== event.id &&
            (e.persistedId
              ? !linkedIds.has(e.persistedId.toString())
              : !!e.code),
        )
        .map((e) => ({
          value: e.persistedId
            ? (e.persistedId as string)
            : `derived::${e.code}`,
          label: e.title,
        })),
    [jobEvents, linkedIds, event.id],
  );

  if (!taskId) {
    return (
      <div className="space-y-2 rounded-md border p-3">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Link2 className="size-3.5" />
          Dependencies
        </span>
        <p className="text-xs text-muted-foreground">
          Assign or restatus this item first to enable dependencies.
        </p>
      </div>
    );
  }

  const addEdge = async (kind: "blockedBy" | "blocks", otherId: string) => {
    try {
      // Derived candidates have no row yet — persist one first so the
      // dependency edge has a real id on both ends.
      const other = otherId.startsWith("derived::")
        ? await materialise({
            jobId: event.jobId,
            code: otherId.slice("derived::".length),
          })
        : (otherId as Id<"jobKeyDates">);
      await createDep(
        kind === "blockedBy"
          ? { blockingTaskId: other, blockedTaskId: taskId }
          : { blockingTaskId: taskId, blockedTaskId: other },
      );
      setAddingKind(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not add dependency."));
    }
  };

  const remove = async (dependencyId: Id<"taskDependencies">) => {
    try {
      await removeDep({ dependencyId });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not remove dependency."));
    }
  };

  const renderGroup = (
    kind: "blockedBy" | "blocks",
    title: string,
    edges: NonNullable<typeof deps>["blockedBy"],
  ) => (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {title}
        </span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5 text-xs"
          onClick={() => setAddingKind(addingKind === kind ? null : kind)}
        >
          <Plus className="size-3.5" />
        </Button>
      </div>

      <ul className="space-y-1">
        {edges.map((edge) => (
          <li
            key={edge.dependencyId}
            className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm"
          >
            {edge.task.isComplete ? (
              <CheckCircle2 className="size-3.5 shrink-0 text-emerald-500" />
            ) : (
              <Ban className="size-3.5 shrink-0 text-amber-500" />
            )}
            {rowByPersistedId.has(edge.task.id.toString()) ? (
              <button
                type="button"
                onClick={() => openRelated(edge.task.id.toString())}
                className="min-w-0 flex-1 truncate text-left hover:text-primary hover:underline"
                title={`Open ${edge.task.title}`}
              >
                {edge.task.title}
              </button>
            ) : (
              <span className="min-w-0 flex-1 truncate">{edge.task.title}</span>
            )}
            {edge.task.statusName && (
              <Badge variant="soft" size="sm">
                {edge.task.statusName}
              </Badge>
            )}
            <button
              type="button"
              onClick={() => remove(edge.dependencyId)}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
              aria-label="Remove dependency"
            >
              <X className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>

      {addingKind === kind && (
        <SearchableSelect
          value=""
          onValueChange={(v) => addEdge(kind, v)}
          options={candidateOptions}
          placeholder="Pick a task…"
          searchPlaceholder="Search tasks…"
          className="h-8 w-full"
        />
      )}
    </div>
  );

  const edgeCount = (deps?.blockedBy.length ?? 0) + (deps?.blocks.length ?? 0);
  // Only claim vertical space once there's a dependency to show or the user
  // has expanded the editor — the empty state is just the header line.
  const open = expanded || edgeCount > 0;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Link2 className="size-3.5 shrink-0" />
          Dependencies
          {edgeCount > 0 && (
            <span className="tabular-nums text-muted-foreground/80">
              {edgeCount}
            </span>
          )}
          {event.blocked && (
            <Badge color="amber" variant="soft" size="sm" className="ml-1">
              Blocked
            </Badge>
          )}
        </span>
        {edgeCount === 0 &&
          (open ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 shrink-0 px-1.5 text-xs"
              aria-label="Collapse dependencies"
              onClick={() => {
                setAddingKind(null);
                setExpanded(false);
              }}
            >
              <ChevronDown className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="-mr-1.5 h-6 shrink-0 gap-1 px-1.5 text-xs text-muted-foreground"
              onClick={() => setExpanded(true)}
            >
              <Plus className="size-3.5" /> Add
            </Button>
          ))}
      </div>
      {open && (
        <>
          {renderGroup("blockedBy", "Blocked by", deps?.blockedBy ?? [])}
          {renderGroup("blocks", "Blocks", deps?.blocks ?? [])}
        </>
      )}
    </div>
  );
}
